import { useApiCall } from "@/app/api/apiCall";
import { PostPayload } from "@/app/api/types";
import AppLayout from "@/app/components/AppLayout";
import DetailPageLayout from "@/app/components/DetailPageLayout";
import { Footer } from "@/app/components/Footer";
import { FormField } from "@/app/components/form-fields/FormField";
import {
  IPrivateContext,
  PrivateContext,
} from "@/app/components/PrivateContextLoader";
import { SubmitButton } from "@/app/components/SubmitButton";
import { useApiContext } from "@/app/contexts/ApiContext";
import { Entity, IField } from "@/app/fields/types";
import { useStandardToast } from "@/app/hooks/useStandardToast";
import { INavLinks, NavContext } from "@/app/NavContext";
import { buildDefaultValues } from "@/app/utils/buildDefaultValues";
import { cleanPayload } from "@/app/utils/cleanPayload";
import { Flex } from "@chakra-ui/react";
import { BackButton, Page, PageBody, PageHeader } from "@saas-ui-pro/react";
import { Form, LoadingSpinner } from "@saas-ui/react";
import { useContext, useState } from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";

export const AddPage = <T extends Entity>() => {
  const { createEntry, getFieldOptions } = useApiContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { fields } = useContext(PrivateContext) as IPrivateContext<T>;
  const resourceNamePlural = pathname.split("/")[1];
  const resourceName = resourceNamePlural
    .slice(0, resourceNamePlural.length - 1)
    .replace("-", " ");
  const { navLinks } = useContext(NavContext);
  const href = `/${resourceNamePlural}` as keyof INavLinks;
  const [isLoading, setIsLoading] = useState(false);

  const handle = useApiCall();
  const toast = useStandardToast();
  const onCreate = async (_payload: PostPayload<T>, fields: IField<T>[]) => {
    setIsLoading(true);
    const payload = cleanPayload(_payload, fields);
    await handle(
      // if createEntry is undefined it should not be possible to access this page
      () => createEntry!({ ...payload }),
      () => {
        toast({
          title: `${resourceName.slice(0, 1).toUpperCase()}${resourceName.slice(
            1
          )} created`,
          status: "success",
        });
        navigate(href);
      },
      () => setIsLoading(false)
    );
  };

  return (
    <AppLayout>
      {!fields ? (
        <Flex
          flexDirection="column"
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <LoadingSpinner />
        </Flex>
      ) : (
        <Form
          onSubmit={(p: PostPayload<T>) => onCreate(p, fields)}
          className="contents"
          defaultValues={buildDefaultValues(fields)}
        >
          <Page>
            <PageHeader
              title={`Add new ${resourceName}`}
              nav={
                <BackButton href={navLinks[href]} data-cy="page-back-button" />
              }
            />
            <PageBody>
              <DetailPageLayout<T>
                fields={fields.filter(({ is_readonly }) => !is_readonly)}
                render={(field) => (
                  <FormField
                    field={field}
                    getFieldOptions={getFieldOptions}
                    isDisabled={isLoading}
                  />
                )}
              />
            </PageBody>
            <Footer>
              <div className="w-full flex justify-end">
                <SubmitButton isDisabled={isLoading}>Create entry</SubmitButton>
              </div>
            </Footer>
          </Page>
        </Form>
      )}
    </AppLayout>
  );
};
